import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Existing elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import Modal from '../elements/modal';
import { getLocalItem, getSessionItem } from '../../utils/storage';
import { startingPriceLabel } from '../../utils/pricing';

// Premium components
import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  BlogCard,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

class BlogListSidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      activeCategory: 'all',
    };
  }

  componentDidMount() {
    if (
      getLocalItem('phone_provided') !== 'yes' &&
      getSessionItem('phone_ignored') !== 'yes' &&
      !this.props.user.is_logged
    ) {
      this.props.setOpenModal(true);
    }
  }

  render() {
    const {
      isMobileBrowser,
      isMobileApp,
      citiesModal,
      setCitiesModal,
      openModal,
      setOpenModal,
      user,
      setUser,
      blogsData = [],
    } = this.props;

    const { currentPage, activeCategory } = this.state;
    const POSTS_PER_PAGE = 9;

    // Extract unique categories
    const categories = ['all', ...new Set(blogsData.map((b) => b.category).filter(Boolean))];

    // Filter by category
    const filtered = activeCategory === 'all'
      ? blogsData
      : blogsData.filter((b) => b.category === activeCategory);

    // Pagination
    const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
    const startIdx = (currentPage - 1) * POSTS_PER_PAGE;
    const currentBlogs = filtered.slice(startIdx, startIdx + POSTS_PER_PAGE);

    // Featured blog (first from full list)
    const featured = blogsData[0];

    // Breadcrumbs
    const breadcrumbs = [
      { label: 'Home', href: '/' },
      { label: 'Blogs' },
    ];

    // Structured data
    const schema = {
      '@type': 'CollectionPage',
      name: 'Ride N Repair Blog',
      description: 'Expert tips, guides, and insights on bike and car maintenance from Ride N Repair.',
      url: 'https://www.ridenrepair.com/blogs',
    };

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <link rel="canonical" href="https://www.ridenrepair.com/blogs" />
          <title>Blog — Expert Bike & Car Service Tips | Ride N Repair</title>
          <meta
            name="description"
            content="Expert tips, guides, and insights on bike and car maintenance. Learn about servicing schedules, DIY tips, and cost-saving advice from Ride N Repair mechanics."
          />
          <meta property="og:title" content="Blog — Expert Bike & Car Service Tips | Ride N Repair" />
          <meta property="og:url" content="https://www.ridenrepair.com/blogs" />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schema} />

        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}
        {!isMobileApp && openModal && (
          <Modal
            isMobileBrowser={isMobileBrowser}
            isMobileApp={isMobileApp}
            openModal={openModal}
            setOpenModal={setOpenModal}
            user={user}
            setUser={setUser}
          />
        )}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        {/* HERO */}
        <PremiumHero
          compact
          badge="Our Blog"
          title="Expert Tips & Guides"
          subtitle="Insights on bike and car maintenance from certified mechanics"
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: 'Book a Service', href: '/service', variant: 'primary' },
          ]}
        />

        <Breadcrumbs items={breadcrumbs} />

        <StickyMobileCTA
          title="Bike & Car Service"
          priceHint={`Starting ${startingPriceLabel('site')} · 30-Day Warranty`}
          btnLabel="Book Now"
          btnHref="/service"
        />

        {/* FEATURED BLOG */}
        {featured && (
          <SectionWrapper kicker="Featured" heading={featured.title}>
            <div className="rr-blog-featured">
              <BlogCard
                slug={featured.slug}
                title={featured.title}
                excerpt={featured.summary || featured.blogText}
                image={featured.thumb}
                category={featured.category}
                date={featured.date}
                readTime={featured.readTime}
              />
            </div>
          </SectionWrapper>
        )}

        {/* CATEGORY FILTER + BLOG GRID */}
        <SectionWrapper variant="gray" kicker="All Articles" heading="Browse Our Blog">
          {/* Category filter pills */}
          {categories.length > 2 && (
            <div className="rr-blog-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`rr-blog-filter-pill${activeCategory === cat ? ' rr-blog-filter-pill--active' : ''}`}
                  onClick={() => this.setState({ activeCategory: cat, currentPage: 1 })}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
          )}

          {/* Blog Grid */}
          <div className="rr-card-grid--3col">
            {currentBlogs.map((blog, i) => (
              <BlogCard
                key={blog.slug || i}
                slug={blog.slug}
                title={blog.title}
                excerpt={blog.summary || blog.blogText}
                image={blog.thumb}
                category={blog.category}
                date={blog.date}
                readTime={blog.readTime}
              />
            ))}
          </div>

          {currentBlogs.length === 0 && (
            <p className="rr-sub" style={{ textAlign: 'center', marginTop: 40 }}>No articles found in this category.</p>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="rr-blog-pagination">
              <button
                className="rr-blog-pagination__btn"
                disabled={currentPage <= 1}
                onClick={() => this.setState({ currentPage: currentPage - 1 })}
              >
                Previous
              </button>
              <span className="rr-blog-pagination__info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="rr-blog-pagination__btn"
                disabled={currentPage >= totalPages}
                onClick={() => this.setState({ currentPage: currentPage + 1 })}
              >
                Next
              </button>
            </div>
          )}
        </SectionWrapper>

        {/* FINAL CTA */}
        <FinalCTA
          heading={<>Need Vehicle Service?<br />{`Book Now — Starting ${startingPriceLabel('site')}.`}</>}
          subtitle="Doorstep service · Certified mechanics · 30-day warranty"
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default BlogListSidebar;
